import React, { useEffect, useState } from 'react';
import { fetchCrops, fetchSymptomsByLevel, fetchDiseases } from '../api/cropService';
import { useTranslation } from 'react-i18next';

const ViewCrop = () => {
  const { t } = useTranslation();
  const [crops, setCrops] = useState([]);
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [symptoms, setSymptoms] = useState([]);
  const [diseases, setDiseases] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadCrops = async () => {
      try {
        const data = await fetchCrops(true);
        setCrops(data);
        if (data.length > 0) setSelectedCrop(data[0]);
      } catch (error) {
        console.error('Error loading crops:', error);
      }
    };
    loadCrops();
  }, []);

  useEffect(() => {
    const loadDetails = async () => {
      if (!selectedCrop) return;
      setLoading(true);
      try {
        const [symptomsData, diseasesData] = await Promise.all([
          fetchSymptomsByLevel(selectedCrop.cid, 1, true),
          fetchDiseases(selectedCrop.cid, true)
        ]);
        setSymptoms(symptomsData);
        setDiseases(diseasesData);
      } catch (error) {
        console.error('Error loading details:', error);
      } finally {
        setLoading(false);
      }
    };
    loadDetails();
  }, [selectedCrop]);

  return (
    <div className="flex h-screen">
      {/* LEFT: Crop Details */}
      <div className="w-2/3 p-6 overflow-y-auto bg-gray-50">
        {loading ? (
          <p className="text-gray-500">{t('expert.viewCrop.loading')}</p>
        ) : selectedCrop ? (
          <>
            {/* Crop Info */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold mb-6 border-b pb-2 text-gray-800">{t('expert.viewCrop.pageTitle')}</h1>

              <div className="grid grid-cols-3 gap-4 text-gray-700 text-base">
                <div className="font-semibold">{t('expert.viewCrop.cropName')}</div>
                <div className="col-span-2">{selectedCrop.name}</div>

                <div className="font-semibold">{t('expert.viewCrop.cropDescription')}</div>
                <div className="col-span-2">{selectedCrop.name}</div>

                <div className="font-semibold">{t('expert.viewCrop.cropImage')}</div>
                <div className="col-span-2">
                  {selectedCrop.imageUrl ? (
                    <img
                      src={selectedCrop.imageUrl}
                      alt={selectedCrop.name}
                      className="w-64 h-auto rounded shadow border"
                    />
                  ) : (
                    <p className="text-sm text-gray-500 italic">{t('expert.viewCrop.noImageAvailable')}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Symptoms */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-2">{t('expert.viewCrop.symptomsSectionTitle')}</h2>
              {symptoms.length === 0 ? (
                <p className="text-gray-600">{t('expert.viewCrop.noSymptomsFound')}</p>
              ) : (
                <ul className="space-y-4">
                  {symptoms.map(sym => (
                    <li key={sym.sid} className="bg-white p-4 rounded shadow-sm border">
                      <p className="font-semibold">{sym.name}</p>
                      <p className="text-gray-700">{sym.qn}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Diseases */}
            <div>
              <h2 className="text-xl font-semibold mb-2">{t('expert.viewCrop.problemsSectionTitle')}</h2>
              {diseases.length === 0 ? (
                <p className="text-gray-600">{t('expert.viewCrop.noProblemsFound')}</p>
              ) : (
                <ul className="space-y-4">
                  {diseases.map(d => (
                    <li key={d.disid} className="p-4 rounded border shadow-sm">
                      <p>
                        {d.chemical_name || d.scientific_name}
                      </p>
                      <p className="text-gray-800">{d.description}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : (
          <p className="text-gray-500">{t('expert.viewCrop.selectCropPrompt')}</p>
        )}
      </div>

      {/* RIGHT: Crop List */}
      <div className="w-1/3 p-6 border-l border-gray-200 overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">{t('expert.viewCrop.existingCrops')}</h2>
        <ul>
          {crops.map(crop => (
            <li
              key={crop.cid}
              onClick={() => setSelectedCrop(crop)}
              className={`cursor-pointer p-3 rounded mb-2 transition ${selectedCrop?.cid === crop.cid
                  ? 'bg-blue-600 text-white'
                  : 'hover:bg-gray-100'
                }`}
            >
              {crop.name}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ViewCrop;